// Croqui: `components/Segmented.tsx`. Abas de lista, presets de período, live/test.

import { cn } from '../../lib/cn.ts';

export interface SegItem {
  id: string;
  label: string;
  count?: number;
  /** Tinta do ponto à esquerda (`ModeToggle`). */
  dot?: 'green' | 'muted';
}

/**
 * Controle segmentado, no MESMO desenho do `components/ui/segmented.tsx` (WEB-133): trilho
 * na célula do sistema, item ativo em pílula de tinta. Só a API é a do kit (contagem e
 * ponto por item).
 *
 * `role="group"` + `aria-pressed`: são botões de filtro, não abas de painel, não há
 * `tabpanel` por trás. `label` vira o nome acessível do grupo.
 */
export function Segmented({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: SegItem[];
  value: string;
  onChange?: (id: string) => void;
  label: string;
  className?: string;
}) {
  return (
    <fieldset
      aria-label={label}
      className={cn(
        // `flex-wrap`: oito abas com contagem passam de 1024 numa linha só (medido em
        // `/admin/withdrawals`: 1393px de `scrollWidth` com `body` em 1024). Por isso o
        // raio é fixo (18px = metade da linha de 36px) e não `rounded-full`: numa linha é
        // pílula, em duas vira um bloco arredondado em vez de um estádio deformado.
        'inline-flex flex-wrap items-center gap-0.5 rounded-[18px] bg-tile p-[3px] in-data-kit-card:in-data-surface:bg-accent',
        className,
      )}
    >
      {items.map((item) => {
        const on = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={on}
            onClick={() => {
              onChange?.(item.id);
            }}
            className={cn(
              'inline-flex min-h-10 items-center gap-1.5 whitespace-nowrap rounded-pill px-3 py-1 text-13 font-medium transition-colors duration-150 sm:min-h-[30px]',
              on ? 'bg-primary text-primary-foreground' : 'text-muted hover:text-text',
            )}
          >
            {item.dot !== undefined && (
              // Quadrado, como toda marca de estado do sistema.
              <span
                aria-hidden="true"
                className={cn(
                  'size-[7px] shrink-0',
                  item.dot === 'green' ? 'bg-green' : 'bg-muted',
                )}
              />
            )}
            {item.label}
            {item.count !== undefined && (
              // Contagem em mono na cor do próprio item (herda): sem chip, que sobre a
              // pílula de tinta viraria um remendo claro.
              <span className="data-value text-12">{item.count}</span>
            )}
          </button>
        );
      })}
    </fieldset>
  );
}
